import { logs } from "./logs.js";

/**
 * Create logging methods for Entity class
 *
 * Instead of a mixin, we create a factory function that generates bound methods.
 * This ensures the methods have proper access to component context.
 */
const createEntityLogger = function(componentInstance) {
  // This must be called within the Entity constructor to ensure proper binding
  const namespace = componentInstance.namespace || 'unknown';

  /**
   * Validate that a node is valid and has a UID
   */
  const validateNode = (cNode, methodName) => {
    if (!cNode || typeof cNode !== 'object') {
      console.error(`[${namespace}] ERROR: Missing or invalid node in ${methodName} call. The first argument must be a node object.`);
      return false;
    }

    if (!cNode.uid) {
      console.error(`[${namespace}] ERROR: Node has no uid property in ${methodName} call. Please ensure you're passing a valid node.`);
      return false;
    }

    return true;
  };

  // Real log function - gets swapped in/out based on filters
  const logFn = function(cNode, message, ...data) {
    if (!validateNode(cNode, 'log')) return;
    logs.log(namespace, cNode.uid, message, ...data);
  };

  // Create the logger object - log will be swapped by register
  const logger = {
    log: logFn,
    // debug always works - needs wrapper for cNode validation
    debug: function(cNode, message, ...data) {
      if (!validateNode(cNode, 'debug')) return;
      logs.debug(namespace, cNode.uid, message, ...data);
    },
    // warn, error, critical always work - need wrappers for cNode validation
    warn: function(cNode, message, ...data) {
      if (!validateNode(cNode, 'warn')) return;
      logs.warn(namespace, cNode.uid, message, ...data);
    },
    error: function(cNode, message, ...data) {
      if (!validateNode(cNode, 'error')) return;
      logs.error(namespace, cNode.uid, message, ...data);
    },
    critical: function(cNode, message, ...data) {
      if (!validateNode(cNode, 'critical')) return;
      logs.critical(namespace, cNode.uid, message, ...data);
    }
  };

  // Register logger - this sets initial log state based on current filters
  logs.register(namespace, logger, logFn);

  return logger;
};

// Export a dummy placeholder that will be replaced with real implementations
// during Entity initialization
const EntityLogger = {
  log: function() { console.error("Error: Logger not properly initialized"); },
  warn: function() { console.error("Error: Logger not properly initialized"); },
  error: function() { console.error("Error: Logger not properly initialized"); },
  critical: function() { console.error("Error: Logger not properly initialized"); },
  debug: function() { console.error("Error: Logger not properly initialized"); }
};

export { EntityLogger, createEntityLogger };